import {useEffect, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {Checkbox} from '@mui/material'
import {TSector} from '../../types/sector.type'
import {useTranslation} from '../../utils/useTranslation'
import {pruneIds} from '../../utils/filters.functions'
import {useListSectorsByNetwork} from '../../hooks/sector.hooks'
import {useSelectedSectorIds, useUIActions} from '../../store'
import {useFilterTracking} from '../../hooks/filterTracking.hooks'
import {
  TLinearView,
  useLinearWindowControls,
} from '../../hooks/networkWindow.hooks'
import PopoverButton from '../Popover'
import '../FilterPopover/styles.css'
import '../../styles/filterPanel.css'
import './styles.css'

type LinearWindowFilterProps = {
  /** Which linear view is active — tags the manual-PK analytics. */
  view: TLinearView
}

/**
 * Designates the linear view's PK window from a single popover with two mutually
 * exclusive sections: pick sectors (multi-select) OR type a custom PK range.
 * The exclusion is enforced by the store (see `useNetworkWindow` /
 * `useLinearWindowControls`); here it is only surfaced — the section that does
 * not own the current window is dimmed, but stays interactive so touching it
 * hands the window over.
 *
 * Self-contained so it can live in the global header, above the data wrapper
 * that consumes the window. The PK inputs display the resolved window for the
 * manual and extent sources (typed value → network extent). A SECTOR window is a
 * path (graph diameter), not a PK hull, so the inputs stay blank with a hint
 * rather than show a misleading front-computed envelope; typing supersedes the
 * sector.
 */
const LinearWindowFilter = ({view}: LinearWindowFilterProps) => {
  const {t} = useTranslation()
  const {networkId} = useParams()

  const {data} = useListSectorsByNetwork(networkId)
  const sectors: TSector[] = useMemo(() => data ?? [], [data])

  const selectedSectorIds = useSelectedSectorIds()
  const {setSelectedSectorIds} = useUIActions()
  const trackFilter = useFilterTracking()

  const {resolved, onBoundsChange} = useLinearWindowControls(
    networkId ?? '',
    view,
  )

  // Self-heal: drop persisted sector ids absent from this network's catalogue
  // (after a network switch or rehydration). Skip while the list is empty — it
  // is also empty during loading, and we must not wipe a valid selection before
  // the sectors have arrived.
  useEffect(() => {
    if (sectors.length === 0) return
    const pruned = pruneIds(
      selectedSectorIds,
      sectors.map(s => s.id),
    )
    if (pruned !== selectedSectorIds) setSelectedSectorIds(pruned)
  }, [sectors, selectedSectorIds, setSelectedSectorIds])

  const toggleSector = (id: string) => {
    const next = selectedSectorIds.includes(id)
      ? selectedSectorIds.filter(sid => sid !== id)
      : [...selectedSectorIds, id]
    setSelectedSectorIds(next)
    trackFilter('sector')
  }

  const allSectorsSelected =
    sectors.length > 0 && selectedSectorIds.length === sectors.length

  // Select every sector at once (or clear them). Like `toggleSector`, this only
  // writes the sector selection — the store's mutual exclusion drops any manual
  // PK window, and the consumer's sector-pick effect resets the window afresh.
  const toggleAllSectors = () => {
    setSelectedSectorIds(allSectorsSelected ? [] : sectors.map(s => s.id))
    trackFilter('sector')
  }

  const commit =
    (field: 'pkStart' | 'pkEnd') =>
    (event: React.FocusEvent<HTMLInputElement>) => {
      const raw = event.target.value.trim()
      // Empty clears this bound (falls back to sector/extent) — NOT 0: an empty
      // string coerces to 0 through Number(), and a 0→0 window returns an empty
      // viewport that would crash the axis builder.
      if (raw === '') {
        onBoundsChange({[field]: undefined})
        return
      }
      const next = Number(raw)
      if (!Number.isFinite(next)) return
      onBoundsChange({[field]: next})
    }

  // A window is designated (sectors or a custom range) — lights the trigger.
  // The two sections are the two ways in, never disabled: the hint below says so,
  // the store enforces the exclusion, so neither is greyed out.
  const isActive = resolved.source !== 'extent'

  // A sector picks its window as a PATH (graph diameter), not a naive PK hull, so
  // the front-computed `resolved.pk{Start,End}` (a min/max envelope) would be a
  // misleading number here. Leave the inputs blank with a hint instead of showing
  // a wrong PK. They stay editable: typing a bound flips to a manual window and
  // drops the sector (mutual exclusion). Extent mode keeps showing the network
  // min/max — a truthful default for a whole-network window.
  const isSectorDriven = resolved.source === 'sector'
  const displayStart = isSectorDriven ? undefined : resolved.pkStart
  const displayEnd = isSectorDriven ? undefined : resolved.pkEnd

  return (
    <PopoverButton
      buttonIcon='sectors'
      buttonText={t('sector:filterLabel')}
      buttonClassName='actionButton actionButtonLabeled'
      isActive={isActive}>
      <div className='lsf-panel'>
        {sectors.length > 0 && (
          <>
            <div className='lsf-section'>
              <div className='lsf-section-header-row'>
                <span className='lsf-section-header'>
                  {t('linearView:sectors')}
                </span>
                <button
                  type='button'
                  className='lsf-select-all'
                  onClick={e => {
                    e.stopPropagation()
                    toggleAllSectors()
                  }}>
                  {t(
                    allSectorsSelected
                      ? 'common:deselectAll'
                      : 'common:selectAll',
                  )}
                </button>
              </div>
              {sectors.map(sector => (
                <div
                  key={sector.id}
                  className='filterItem'
                  onClick={e => {
                    // Keep the popover open on toggle (multi-select) — the
                    // wrapping Popover closes on any bubbling click.
                    e.stopPropagation()
                    toggleSector(sector.id)
                  }}>
                  <div className='filterLabelWrapper'>
                    <label>{sector.name || sector.acronym}</label>
                  </div>
                  <Checkbox
                    onChange={e => e.stopPropagation()}
                    checked={selectedSectorIds.includes(sector.id)}
                    sx={{'&': {padding: '0px'}}}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        <div
          className='lsf-section'
          // Typing/focusing the inputs must not close the popover.
          onClick={e => e.stopPropagation()}>
          {/* When sectors precede it, the header carries the "OR" — it IS the
              divider, spelling out the mutual exclusion. Alone, it's a plain
              header. */}
          <div className={sectors.length > 0 ? 'lsf-or' : 'lsf-section-header'}>
            {t(
              sectors.length > 0
                ? 'linearView:orCustomRange'
                : 'linearView:customRange',
            )}
          </div>
          <div className='lsf-fields'>
            <label className='nwf-field'>
              <span className='nwf-label'>{t('linearView:pkStart')}</span>
              <input
                className='nwf-input field-shell'
                type='number'
                // Keyed on the value so a reset/sector-pick re-mounts the uncontrolled input.
                key={`start-${isSectorDriven ? 'sector' : (resolved.pkStart ?? '')}`}
                defaultValue={displayStart ?? ''}
                placeholder={isSectorDriven ? '1000' : undefined}
                onBlur={commit('pkStart')}
              />
            </label>
            <label className='nwf-field'>
              <span className='nwf-label'>{t('linearView:pkEnd')}</span>
              <input
                className='nwf-input field-shell'
                type='number'
                key={`end-${isSectorDriven ? 'sector' : (resolved.pkEnd ?? '')}`}
                defaultValue={displayEnd ?? ''}
                placeholder={isSectorDriven ? '7000' : undefined}
                onBlur={commit('pkEnd')}
              />
            </label>
          </div>
        </div>
      </div>
    </PopoverButton>
  )
}

export default LinearWindowFilter
